"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionFormModalCtrl = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const mobx_1 = require("mobx");
const services_1 = require("../../services");
const log_service_1 = require("../../services/log.service");
const page_state_1 = require("../../state/page.state");
const store_1 = require("../../store");
class CollectionFormModalCtrl {
    get hasWarnings() {
        return Boolean(this.warnings.length);
    }
    constructor(collections) {
        this.collectionStats = [];
        this.failures = [];
        this.warnings = [];
        this.updatingForm = false;
        this.collections = collections;
    }
    updateFormCollections() {
        return tslib_1.__awaiter(this, arguments, void 0, function* (forceUpdate = false) {
            this.setUpdatingForm(true);
            this.failures = [];
            this.warnings = [];
            const outputs = [];
            for (const stat of this.collectionStats) {
                if (!stat.latestFormRevision) {
                    return;
                }
                if (stat.actualFormRevision === stat.latestFormRevision) {
                    continue;
                }
                const formModel = yield store_1.AppStore.Form.fetch(stat.latestFormRevision);
                const collectionModel = yield store_1.AppStore.Collection.fetch(stat.collectionRevision);
                if (!collectionModel) {
                    continue;
                }
                const upgrade = core_shared_1.IkeModel.Collection.upgrade([collectionModel], formModel);
                const output = yield upgrade.process(log_service_1.Logger);
                if (output.result !== 'success') {
                    output.fields.forEach((field) => {
                        this.failures.push({
                            failure: field,
                            collectionId: stat.collectionId,
                            collectionTitle: stat.collectionTitle,
                        });
                    });
                    // When forcing, still queue the collection for save (save endpoint will strip invalid data)
                    if (forceUpdate) {
                        outputs.push({ collectionModel, targetFormRevision: stat.latestFormRevision });
                    }
                }
                else {
                    outputs.push({ collectionModel, targetFormRevision: stat.latestFormRevision });
                }
            }
            // If there are failures and not forcing, show warning state instead of blocking
            if (this.failures.length > 0 && !forceUpdate) {
                this.warnings = [...this.failures];
                this.setUpdatingForm(false);
                return; // Return early - modal will detect hasWarnings and show warning UI
            }
            // Save collections with swapped formRevision - save endpoint will strip invalid data
            const newCollectionUuids = [];
            for (const { collectionModel, targetFormRevision } of outputs) {
                const savedCollection = yield this.saveCollectionWithFormRevision(collectionModel, targetFormRevision);
                newCollectionUuids.push(savedCollection.uuid);
            }
            yield page_state_1.CurrentState.updateInspectedCollection();
            yield this.refresh(newCollectionUuids);
            this.setUpdatingForm(false);
        });
    }
    saveCollectionWithFormRevision(collectionModel, targetFormRevision) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // Fetch new and old model
            const sourceFormModel = yield store_1.AppStore.Form.fetch(collectionModel.schema.version);
            const targetFormModel = yield store_1.AppStore.Form.fetch(targetFormRevision);
            const allTargetFields = new Set(targetFormModel.getFieldsRecursive.map((field) => field.id));
            // Create a deep clone of the collection object to not mess with active mobx state
            const collectionJson = yield core_shared_1.IkeModel.Collection.write(collectionModel);
            const dupedCollectionModel = core_shared_1.IkeModel.Collection.read(collectionJson, sourceFormModel, collectionModel.loader);
            dupedCollectionModel.forEachField((field) => {
                // If the target form doesn't contain the current field, then drop this fields contents
                if (!allTargetFields.has(field.schema.id)) {
                    field.Mutator.clear();
                }
            });
            const targetCollectionJson = yield core_shared_1.IkeModel.Collection.write(dupedCollectionModel);
            // Swap the form version to target form - save endpoint will strip invalid data
            targetCollectionJson.formversion = targetFormRevision;
            targetCollectionJson.data.version = targetFormRevision;
            const rest = services_1.Services.Department.rest;
            const savedCollection = yield rest.save(rest.Objects.Collection, targetCollectionJson, {
                formUpdated: {
                    fromRevision: sourceFormModel.version,
                    fromVersion: sourceFormModel.state.displayVersion,
                    toVersion: targetFormModel.state.displayVersion,
                },
            });
            return savedCollection;
        });
    }
    refresh(collectionUuids) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const collectionSummaries = yield Promise.all(collectionUuids.map((uuid) => store_1.AppStore.CollectionSummary.getFetchableByUuid(uuid).refetch()));
            return Promise.all([
                store_1.AppStore.Tag.refresh(),
                ...this.getTagIds(collectionSummaries).map((tagId) => store_1.AppStore.TagSummary.getFetchable(tagId).refetch()),
            ]);
        });
    }
    getTagIds(collectionSummaries) {
        const tags = {};
        for (const collectionSummary of collectionSummaries) {
            for (const tag of collectionSummary.tags) {
                if (!tags[tag] && !(0, core_api_1.isSystemTag)(tag)) {
                    tags[tag] = true;
                }
            }
        }
        return Object.keys(tags);
    }
    setUpdatingForm(updatingForm) {
        this.updatingForm = updatingForm;
    }
    getCollectionStats(departmentForms) {
        const collectionStats = [];
        this.collections.forEach((collectionSummary) => {
            const formSummary = store_1.AppStore.FormSummary.get(collectionSummary.formRevision);
            const stat = {
                collectionId: collectionSummary.collectionId,
                collectionTitle: collectionSummary.title,
                collectionRevision: collectionSummary.revision,
                formUuid: formSummary ? formSummary.uuid : '',
                actualFormRevision: collectionSummary.formRevision,
                actualFormDate: formSummary && formSummary.updatedAt ? formSummary.updatedAt : '',
            };
            collectionStats.push(stat);
        });
        collectionStats.forEach((stat) => {
            const form = stat.formUuid ? departmentForms.find(({ uuid }) => uuid === stat.formUuid) : null;
            if (form) {
                stat.latestFormRevision = form.revision;
                stat.latestFormName = form.displayname;
                stat.latestFormDate = form.updatedAt;
            }
        });
        this.collectionStats = collectionStats;
        return collectionStats;
    }
}
exports.CollectionFormModalCtrl = CollectionFormModalCtrl;
tslib_1.__decorate([
    mobx_1.observable
], CollectionFormModalCtrl.prototype, "warnings", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionFormModalCtrl.prototype, "updatingForm", void 0);
tslib_1.__decorate([
    mobx_1.computed
], CollectionFormModalCtrl.prototype, "hasWarnings", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionFormModalCtrl.prototype, "setUpdatingForm", null);
//# sourceMappingURL=collection.form.modal.ctrl.js.map